// Auth context: holds the logged-in user, handles login/logout, and restores
// the session from a saved token on page load.
import { createContext, useContext, useEffect, useState } from "react";
import { api, getToken, setToken, clearToken } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount, if a token exists, fetch the profile to confirm it's still valid.
  useEffect(() => {
    let active = true;
    (async () => {
      if (!getToken()) { setLoading(false); return; }
      try {
        const { user } = await api.me();
        if (active) setUser(user);
      } catch {
        clearToken();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Keep the profile fresh while the session is open. `isManager`/`accessRole`
  // are recomputed server-side, so when HR promotes/removes a manager, the
  // affected user's Team tabs appear/disappear on their next tab focus — no
  // re-login needed. Failures are ignored (don't log out on a transient refetch).
  useEffect(() => {
    const refresh = async () => {
      if (!getToken()) return;
      try {
        const { user } = await api.me();
        setUser(user);
      } catch { /* ignore transient errors */ }
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const login = async (email, password) => {
    const { token, user } = await api.login(email, password);
    setToken(token);
    setUser(user);
    return user;
  };

  const logout = async () => {
    // Record the LOGOUT server-side while the token is still valid (best-effort —
    // a failed call must never trap the user in the app), then drop the session.
    try { await api.logout(); } catch { /* ignore — log out regardless */ }
    clearToken();
    setUser(null);
  };

  const isHR = user?.accessRole === "HR";
  const isManager = !!user?.isManager; // has at least one direct report → sees team tabs

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isHR, isManager, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
